import React from "react";
import { isFunction, map } from "lodash";
import styled, { css } from "styled-components";
import { shorten } from "@backpacker/number";
import TooltipContent from "@charty/tooltip/serie";
import {
  FontWeight,
  Radius,
  borderRadius,
  form,
  transparentize,
} from "@glossy";
import { Locale } from "@linkfluence/models/locale";
import Tooltip from "@ui-kit/tooltip";
import { callRangeValue } from "./data";
import { IBar, Placement } from "./model";

interface IProps {
  bars: IBar[];
  locale: Locale;
  onClick: (range: string, gender: string) => void;
  tooltip: (serie: any) => React.ReactNode;
  placement: Placement;
  hovered: string;
}

const StyledTd = styled.td<{
  interactive?: boolean;
  placement?: Placement;
  legend?: boolean;
}>`
  text-align: center;
  padding: 0 6px;
  padding-bottom: ${(props) => props.placement === "top" && "6px"};
  padding-top: ${(props) => props.placement === "bottom" && "6px"};
  width: calc(80% / 7);
  border: none;
  height: 39%;
  cursor: ${(props) => props.interactive && "pointer"};
  position: relative;
  vertical-align: ${(props) => props.placement === "top" && "bottom"};
  vertical-align: ${(props) => props.placement === "bottom" && "top"};
  &:hover {
    background: ${(props) => transparentize(0.85, form(props, "primary"))};
  }
`;

const Bar = styled.div<{
  shadow?: boolean;
  interactive?: boolean;
  height?: number;
  background?: string;
  placement: Placement;
}>`
  transition: background 0.8s ease;
  height: ${(props) => props.height}%;
  background: ${(props) => props.background};
  position: relative;
  cursor: ${(props) => props.interactive && "var(--cursor-magic-wand)"};
  ${(props) => borderRadius(props.placement, Radius.Small)};
  ${(props) =>
    props.shadow &&
    css`
      position: absolute;
      border-top: none;
      top: -1px;
    `};
`;

const BarLegend = styled.div<{
  color: string;
  placement: Placement;
  $display?: boolean;
}>`
  color: ${(props) => props.color};
  height: 24px;
  line-height: 24px;
  position: absolute;
  width: 100%;
  font-size: 14px;
  font-weight: ${FontWeight.Normal};
  ${(props) => (props.placement === "top" ? "bottom" : "top")}: 100%;
  transition: opacity 0.8s ease;
  opacity: ${(props) => (props.$display ? 1 : 0)};
`;

const DemographyBars = ({
  bars,
  locale,
  onClick,
  tooltip,
  placement,
  hovered,
}: IProps) => (
  <>
    {map(bars, (bar, idx) => (
      <StyledTd placement={placement} key={idx}>
        {map(bar.data, (entry, idx) => (
          <Tooltip
            key={idx}
            tooltip={
              tooltip ? (
                tooltip(bar.tooltip)
              ) : (
                <TooltipContent
                  label={bar.tooltip.label}
                  color={bar.tooltip.color}
                  count={bar.tooltip.count}
                  locale={locale}
                  shorten={false}
                />
              )
            }
          >
            <Bar
              shadow={entry.dashed}
              interactive={isFunction(onClick)}
              height={entry.height}
              background={
                !hovered || hovered === entry.id
                  ? entry.color
                  : entry.fadedColor
              }
              key={entry.id}
              onClick={callRangeValue(onClick, bar)}
              placement={placement}
            >
              {entry.percent > 0 && (
                <BarLegend
                  color={entry.color}
                  placement={placement}
                  $display={!hovered || hovered === entry.id}
                >
                  {!entry.dashed && shorten(locale, entry.percent) + "%"}
                </BarLegend>
              )}
            </Bar>
          </Tooltip>
        ))}
      </StyledTd>
    ))}
  </>
);

export default DemographyBars;
